import { MailOutlined } from '@ant-design/icons';
import { Steps, Tag, Tooltip } from 'antd';
import payrollApi from 'api/payrollApi';
import { useAppDispatch } from 'app/hooks';
import { remindApprovePayroll } from 'features/payroll/payrollAction';
import {
  confirmRemind,
  ProgressStepView,
  ProgressView,
} from 'features/payroll/utils/confirmRemind';
import moment from 'moment';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import CustomButton from 'styles/buttonStyled';

interface Props {
  payrollId: number;
  /** đổi giá trị này (vd status pay run) để tải lại sau khi gửi duyệt/duyệt */
  refreshKey?: string;
}

const STATUS_MAP: Record<ProgressStepView['state'], 'finish' | 'process' | 'wait'> = {
  done: 'finish',
  current: 'process',
  upcoming: 'wait',
};

const ApprovalProgress = ({ payrollId, refreshKey }: Props) => {
  const { t } = useTranslation(['payroll', 'action']);
  const dispatch = useAppDispatch();
  const [progress, setProgress] = useState<ProgressView | null>(null);
  const [, forceTick] = useState(0);

  const load = useCallback(async () => {
    if (!payrollId) return;
    try {
      const res: any = await payrollApi.getApprovalProgress(payrollId);
      setProgress(res?.data || null);
    } catch (e) {
      setProgress(null);
    }
  }, [payrollId]);

  useEffect(() => {
    load();
  }, [load, refreshKey]);

  // cập nhật trạng thái nút khi hết cooldown (không cần reload trang)
  useEffect(() => {
    const timer = setInterval(() => forceTick((n) => n + 1), 30000);
    return () => clearInterval(timer);
  }, []);

  if (!progress || !progress.steps?.length) return null;

  const onResend = (step: ProgressStepView) =>
    confirmRemind(t, progress, step, () =>
      dispatch(
        remindApprovePayroll({
          id: payrollId,
          stepStatus: step.statusName,
          callBack: (data: any) => setProgress(data?.progress || progress),
        }),
      ),
    );

  const renderDescription = (step: ProgressStepView) => {
    if (step.state === 'done') {
      return (
        <>
          <Tag color="green">{t('payroll:step_approved')}</Tag>
          {step.approvedBy && step.approvedAt && (
            <div>
              {t('payroll:approved_by_at', {
                name: step.approvedBy.fullName,
                time: moment(step.approvedAt).format('DD/MM/YYYY HH:mm'),
              })}
            </div>
          )}
        </>
      );
    }
    const approverNames = step.approvers.map((a) => a.fullName || a.email).join(', ');
    if (step.state === 'upcoming') {
      return <div>{approverNames}</div>;
    }
    const availableAt = step.remindAvailableAt ? moment(step.remindAvailableAt) : null;
    const waiting = availableAt && availableAt.isAfter(moment());
    return (
      <>
        <Tag color="orange">{t('payroll:step_waiting')}</Tag>
        <div>{approverNames}</div>
        {step.statusName !== 'close' && (
          <Tooltip
            title={waiting ? t('payroll:resend_wait', { time: availableAt.format('HH:mm') }) : ''}
          >
            <CustomButton
              aType="outline"
              style={{ marginTop: 6 }}
              disabled={!!waiting || !step.approvers.length}
              onClick={() => onResend(step)}
            >
              <MailOutlined /> {t('payroll:resend_email')}
            </CustomButton>
          </Tooltip>
        )}
      </>
    );
  };

  return (
    <div style={{ background: '#fff', padding: '12px 16px', marginBottom: 12, borderRadius: 6 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
        <b>{t('payroll:approval_progress')}</b>
        {progress.isCompleted && <Tag color="green">{t('payroll:approval_completed')}</Tag>}
      </div>
      <Steps size="small">
        {progress.steps.map((step) => (
          <Steps.Step
            key={step.nodeId}
            title={step.label}
            status={STATUS_MAP[step.state]}
            description={renderDescription(step)}
          />
        ))}
      </Steps>
    </div>
  );
};

export default ApprovalProgress;
